import React, { useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyles";
import { SiteName } from "../../global/SiteName";
import logo from "../../logo.png";
import {
  FaGlobeAmericas,
  FaPlane,
  FaShip,
  FaTruck,
  FaWarehouse,
  FaShieldAlt,
  FaChartLine,
  FaHandshake,
  FaLeaf,
  FaAward,
  FaCogs,
  FaBalanceScale,
} from "react-icons/fa";
import Footer from "../../components/footers/Footer";
import AOS from "aos";
import "aos/dist/aos.css";

const AboutUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: false,
      offset: 100,
    });
  }, []);

  return (
    <div
      className={`w-full flex flex-col bg-light dark:bg-dark ${CommonClasses.transition}`}
    >
      {/* Hero Section */}
      <div className={`w-full bg-default bg-center bg-cover lg:bg-fixed`}>
        <div className="pb-40 pt-56 px-4 flex flex-col items-center backdrop-blur-sm bg-black/70">
          <img
            src={logo}
            alt={SiteName}
            className="h-24 w-auto mb-8"
            data-aos="fade-up"
          />
          <h1
            className={`${GlobalStyles.headerPrimary} mb-6 text-center`}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            About {SiteName}
          </h1>
          <p
            className="text-gray-700 dark:text-gray-300 max-w-3xl text-center text-lg mb-8"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Pioneering integrated logistics solutions since 2010, {SiteName} has
            established itself as a global leader in multimodal transportation,
            offering comprehensive air, sea, and land freight services that
            connect businesses to opportunities worldwide.
          </p>
        </div>
      </div>

      {/* Company Overview */}
      <div className={`w-full py-20`}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <h2 className={`${GlobalStyles.headerPrimary} mb-6`}>
                Our Global Logistics Network
              </h2>
              <div className="space-y-4">
                <p className={`${GlobalStyles.textSecondary}`}>
                  Founded in 2010, {SiteName} began as a specialized freight
                  forwarding company and has evolved into a comprehensive
                  logistics powerhouse with operations spanning six continents.
                  Our growth trajectory reflects our commitment to innovation,
                  customer-centric solutions, and sustainable supply chain
                  management.
                </p>
                <p className={`${GlobalStyles.textSecondary}`}>
                  Today, we manage over 500,000 shipments annually, operating
                  through a network of 75 strategically located offices and
                  partnering with more than 200 carriers worldwide. Our
                  integrated approach combines the speed of air freight, the
                  capacity of sea transport, and the flexibility of ground
                  logistics to deliver optimal solutions for diverse cargo
                  requirements.
                </p>
                <p className={`${GlobalStyles.textSecondary}`}>
                  Our technological infrastructure includes real-time tracking
                  systems, automated customs clearance platforms, and predictive
                  analytics tools that ensure transparency and efficiency
                  throughout the shipping lifecycle.
                </p>
              </div>
            </div>
            <div
              className="rounded-2xl overflow-hidden shadow-2xl"
              data-aos="fade-left"
              data-aos-delay="200"
            >
              <div className="w-full h-80 bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <FaGlobeAmericas className="text-6xl mx-auto mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Global Reach</h3>
                  <p className="text-white/90">
                    Connecting 150+ countries through our integrated network
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transportation Services Section */}
      <div className="w-full py-20 bg-gray-50 dark:bg-gray-900/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className={`${GlobalStyles.headerPrimary} mb-6`}>
              Comprehensive Transportation Solutions
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg">
              We offer tailored multimodal logistics strategies designed to
              optimize cost, transit time, and cargo security across all
              transportation modes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Air Freight */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
              data-aos="zoom-in"
              data-aos-delay="100"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-6">
                <FaPlane className="text-3xl text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Air Transportation
              </h3>
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  Our air freight division provides expedited global delivery
                  with next-flight-out capabilities and charter services for
                  time-sensitive shipments.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 dark:text-blue-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Express air cargo services
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 dark:text-blue-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Charter flight arrangements
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 dark:text-blue-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Temperature-controlled air transport
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 dark:text-blue-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Dangerous goods handling certified
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Sea Freight */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-xl mb-6">
                <FaShip className="text-3xl text-teal-600 dark:text-teal-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Ocean Transportation
              </h3>
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  Full-container load (FCL) and less-than-container load (LCL)
                  services with dedicated port operations and customs brokerage.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-teal-600 dark:text-teal-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      FCL and LCL container services
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 dark:text-teal-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Roll-on/roll-off (RoRo) transport
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 dark:text-teal-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Breakbulk and project cargo
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 dark:text-teal-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Refrigerated container (Reefer) services
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Land Transportation */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
              data-aos="zoom-in"
              data-aos-delay="300"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-xl mb-6">
                <FaTruck className="text-3xl text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Land Transportation
              </h3>
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  Comprehensive domestic and cross-border road and rail
                  solutions with specialized equipment for diverse cargo
                  requirements.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 dark:text-green-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Full truckload (FTL) and LTL services
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 dark:text-green-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Intermodal rail transport
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 dark:text-green-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Oversized and heavy haul transport
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 dark:text-green-400 mr-2">
                      ✓
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Dedicated fleet management
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Capabilities & Infrastructure */}
      <div className={`w-full py-20`}>
        <div className="container mx-auto px-4">
          <h2
            className={`${GlobalStyles.headerPrimary} mb-12 text-center`}
            data-aos="fade-up"
          >
            Our Capabilities & Infrastructure
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div
              className="text-center p-6"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-2xl mb-6">
                <FaWarehouse className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Warehousing
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                2.5 million sq ft of modern warehousing space with automated
                inventory systems
              </p>
            </div>

            <div
              className="text-center p-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-secondary/10 rounded-2xl mb-6">
                <FaShieldAlt className="text-3xl text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Security
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                ISO 28000 certified security management with 24/7 surveillance
              </p>
            </div>

            <div
              className="text-center p-6"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-2xl mb-6">
                <FaChartLine className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Technology
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                AI-powered logistics platform with predictive analytics and IoT
                integration
              </p>
            </div>

            <div
              className="text-center p-6"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-secondary/10 rounded-2xl mb-6">
                <FaCogs className="text-3xl text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Equipment
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Modern fleet with GPS tracking, temperature control, and
                specialized handling
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Values & Principles */}
      <div className="w-full py-20 bg-gradient-to-br from-primary/5 to-secondary/5 dark:from-primary/10 dark:to-secondary/10">
        <div className="container mx-auto px-4">
          <h2
            className={`${GlobalStyles.headerPrimary} mb-12 text-center`}
            data-aos="fade-up"
          >
            Our Core Values & Principles
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              data-aos="flip-left"
              data-aos-delay="100"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <FaHandshake className="text-xl text-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Integrity
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                We maintain the highest ethical standards in all business
                dealings, ensuring transparency and building lasting trust with
                partners worldwide.
              </p>
            </div>

            <div
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              data-aos="flip-left"
              data-aos-delay="200"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mr-4">
                  <FaLeaf className="text-xl text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Sustainability
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Implementing eco-friendly logistics solutions and reducing
                carbon footprint through optimized routing and sustainable
                practices.
              </p>
            </div>

            <div
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              data-aos="flip-left"
              data-aos-delay="300"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <FaAward className="text-xl text-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Excellence
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Continuous improvement in service delivery, operational
                efficiency, and customer satisfaction through innovation and
                expertise.
              </p>
            </div>

            <div
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              data-aos="flip-left"
              data-aos-delay="400"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mr-4">
                  <FaBalanceScale className="text-xl text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Reliability
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Consistently delivering on promises with 99.8% on-time
                performance and robust contingency planning for supply chain
                resilience.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Industry Recognition */}
      <div className={`w-full py-20`}>
        <div className="container mx-auto px-4">
          <h2
            className={`${GlobalStyles.headerPrimary} mb-12 text-center`}
            data-aos="fade-up"
          >
            Industry Recognition & Partnerships
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div
                  className="text-center"
                  data-aos="zoom-in"
                  data-aos-delay="100"
                >
                  <div className="text-4xl font-bold text-primary mb-2">
                    ISO 9001
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">
                    Quality Management
                  </div>
                </div>
                <div
                  className="text-center"
                  data-aos="zoom-in"
                  data-aos-delay="200"
                >
                  <div className="text-4xl font-bold text-secondary mb-2">
                    IATA
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">
                    Air Cargo Certified
                  </div>
                </div>
                <div
                  className="text-center"
                  data-aos="zoom-in"
                  data-aos-delay="300"
                >
                  <div className="text-4xl font-bold text-primary mb-2">
                    FIATA
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">
                    International Federation
                  </div>
                </div>
                <div
                  className="text-center"
                  data-aos="zoom-in"
                  data-aos-delay="400"
                >
                  <div className="text-4xl font-bold text-secondary mb-2">
                    GSV
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">
                    Security Verified
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center" data-aos="fade-up">
              <p className="text-gray-600 dark:text-gray-400">
                Partnered with leading global carriers including Maersk, DHL,
                FedEx, and UPS to provide comprehensive logistics solutions.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;
